import Fuse from 'fuse.js';

// Common colloquial terms → acronyms/names that live on the department row.
// When a query token matches an alias key, we expand the search with the aliased term
// so "unemployment" finds EDD, "prison" finds CDCR, etc.
const ALIAS_MAP = {
  unemployment: 'EDD employment development',
  jobless: 'EDD',
  disability: 'EDD disability insurance',
  'driver license': 'DMV motor vehicles',
  'drivers license': 'DMV motor vehicles',
  dmv: 'motor vehicles',
  prison: 'CDCR corrections rehabilitation',
  prisons: 'CDCR corrections',
  inmate: 'CDCR corrections',
  corrections: 'CDCR',
  'highway patrol': 'CHP',
  police: 'CHP highway patrol justice',
  highways: 'Caltrans transportation',
  freeway: 'Caltrans transportation',
  roads: 'Caltrans transportation',
  transportation: 'Caltrans',
  tax: 'FTB CDTFA franchise tax board',
  taxes: 'FTB CDTFA',
  'income tax': 'FTB franchise tax',
  'sales tax': 'CDTFA',
  welfare: 'DSS social services CalWORKs',
  calfresh: 'DSS social services',
  foodstamps: 'DSS social services CalFresh',
  'food stamps': 'DSS social services CalFresh',
  medical: 'DHCS health care medi-cal',
  medicaid: 'DHCS medi-cal health care',
  'medi-cal': 'DHCS health care',
  health: 'CDPH DHCS public health',
  schools: 'CDE education',
  school: 'CDE education',
  teachers: 'CDE education',
  education: 'CDE',
  'attorney general': 'DOJ justice',
  justice: 'DOJ attorney general',
  procurement: 'DGS general services',
  water: 'DWR water resources',
  fire: 'CalFire forestry fire protection',
  forestry: 'CalFire',
  veterans: 'CDVA veterans affairs',
  'workers comp': 'DIR industrial relations',
  labor: 'DIR industrial relations',
  banks: 'DFPI financial protection',
  banking: 'DFPI financial protection',
  licensing: 'DCA consumer affairs',
  agriculture: 'CDFA food agriculture',
  farming: 'CDFA food agriculture',
  finance: 'DOF',
  hr: 'CalHR human resources',
};

// Expand a search query by appending any matched aliases. Matching is done
// against whole-word tokens and against the full query string.
export function expandQuery(query) {
  if (!query) return '';
  const q = query.toLowerCase().trim();
  const expansions = new Set([q]);

  // Whole-phrase match first (handles "driver license", "attorney general")
  for (const [key, value] of Object.entries(ALIAS_MAP)) {
    if (q.includes(key)) expansions.add(value.toLowerCase());
  }

  // Token-level match for single words
  for (const token of q.split(/\s+/)) {
    if (ALIAS_MAP[token]) expansions.add(ALIAS_MAP[token].toLowerCase());
  }

  return Array.from(expansions).join(' ');
}

const FUSE_OPTIONS = {
  includeScore: true,
  threshold: 0.4,
  ignoreLocation: true,
  useExtendedSearch: false,
  minMatchCharLength: 1,
  keys: [
    { name: 'acronym', weight: 1.0 },
    { name: 'department_code', weight: 0.9 },
    { name: 'department_number', weight: 0.9 },
    { name: 'bu_code', weight: 0.9 },
    { name: 'common_name', weight: 0.8 },
    { name: 'official_name', weight: 0.8 },
    { name: 'nicknames', weight: 0.7 },
    { name: 'alternative_names', weight: 0.7 },
    { name: 'abbreviations', weight: 0.7 },
    { name: 'search_keywords', weight: 0.5 },
  ],
};

// Build a Fuse index. Call this whenever the department list changes.
export function buildFuse(departments) {
  return new Fuse(departments || [], FUSE_OPTIONS);
}

// Normalize a code-like string: strip leading zeros, lowercase.
// "0690" -> "690", "DEPT-07" -> "dept-07"
function normalizeCode(v) {
  if (v == null) return '';
  const s = String(v).trim().toLowerCase();
  // Only strip leading zeros from purely numeric strings so "0690" === "690"
  return /^\d+$/.test(s) ? s.replace(/^0+/, '') || '0' : s;
}

const CODE_FIELDS = ['bu_code', 'department_code', 'department_number', 'acronym'];

// Match a query against numeric/code fields with leading-zero tolerance.
// Exact matches rank above prefix matches above substring matches.
function codeMatch(departments, query) {
  const q = normalizeCode(query);
  if (!q) return [];
  const exact = [];
  const prefix = [];
  const substr = [];
  for (const d of departments) {
    let bestRank = 3; // 0=exact, 1=prefix, 2=substr, 3=none
    for (const f of CODE_FIELDS) {
      const v = normalizeCode(d[f]);
      if (!v) continue;
      if (v === q) { bestRank = Math.min(bestRank, 0); break; }
      if (v.startsWith(q)) bestRank = Math.min(bestRank, 1);
      else if (v.includes(q)) bestRank = Math.min(bestRank, 2);
    }
    if (bestRank === 0) exact.push(d);
    else if (bestRank === 1) prefix.push(d);
    else if (bestRank === 2) substr.push(d);
  }
  return [...exact, ...prefix, ...substr];
}

// Run a search: returns departments ranked by Fuse score,
// with a code-field fast-path for numeric/short-code queries.
export function searchDepartments(fuse, query, departments = []) {
  if (!fuse || !query) return null;
  const trimmed = query.trim();

  // For purely numeric queries, codes are what the user wants — skip fuzzy entirely.
  if (/^\d+$/.test(trimmed)) {
    return codeMatch(departments, trimmed);
  }

  // For short all-alpha queries (<=5 chars), prioritize code/acronym matches ahead of fuzzy.
  const expanded = expandQuery(trimmed);
  const fuseResults = fuse.search(expanded).map(r => r.item);

  if (trimmed.length <= 5) {
    const codeHits = codeMatch(departments, trimmed);
    const seen = new Set(codeHits.map(d => d.department_id));
    return [...codeHits, ...fuseResults.filter(d => !seen.has(d.department_id))];
  }

  return fuseResults;
}

// Sort helper: pinned-first (in user-specified order), then has_data, then total_spend desc, then name.
export function sortDepartments(departments, pinnedIds = [], pinnedOrder = []) {
  const pinnedSet = new Set(pinnedIds.map(String));

  // Establish explicit pinned ordering
  const orderSource = pinnedOrder.length > 0 ? pinnedOrder : pinnedIds;
  const pinnedRank = new Map();
  orderSource.forEach((id, i) => pinnedRank.set(String(id), i));
  // Any pinned id not in orderSource goes to the end of the pinned block
  pinnedIds.forEach(id => {
    const s = String(id);
    if (!pinnedRank.has(s)) pinnedRank.set(s, pinnedRank.size);
  });

  return [...departments].sort((a, b) => {
    const aPinned = pinnedSet.has(String(a.department_id));
    const bPinned = pinnedSet.has(String(b.department_id));
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    if (aPinned && bPinned) {
      return (pinnedRank.get(String(a.department_id)) ?? 0) - (pinnedRank.get(String(b.department_id)) ?? 0);
    }
    // Non-pinned: has_data first
    const aHas = !!a.has_data || (a.record_count || 0) > 0;
    const bHas = !!b.has_data || (b.record_count || 0) > 0;
    if (aHas !== bHas) return aHas ? -1 : 1;
    // Then by total_spend desc
    const aSpend = Number(a.total_spend) || 0;
    const bSpend = Number(b.total_spend) || 0;
    if (aSpend !== bSpend) return bSpend - aSpend;
    // Fallback: official_name
    return (a.official_name || '').localeCompare(b.official_name || '');
  });
}
